import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
    sent: "text-[#00e676]",
    duplicate_skipped: "text-[#a3a3a3]",
    failed: "text-[#ff3333]",
    unconfigured: "text-[#ff9500]",
    skipped_no_email: "text-[#ff9500]",
};

export default function AdminDigests() {
    const [venues, setVenues] = useState([]);
    const [users, setUsers] = useState([]);
    const [venueId, setVenueId] = useState("");
    const [userId, setUserId] = useState("");
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastResult, setLastResult] = useState(null);

    const loadHistory = () => {
        setLoading(true);
        endpoints.adminDigestHistory(50).then((d) => setHistory(d.history || [])).finally(() => setLoading(false));
    };
    useEffect(() => {
        endpoints.adminVenues({ limit: 500 }).then((d) => setVenues(d.venues || []));
        endpoints.adminUsers({ limit: 500 }).then((d) => setUsers(d.users || []));
        loadHistory();
    }, []);

    const triggerTest = async () => {
        if (!venueId) { toast.error("Select a venue"); return; }
        setBusy(true);
        try {
            const r = await endpoints.adminTestDigest({ venue_id: venueId, user_id: userId || undefined });
            setLastResult(r);
            toast.success(`Test digest → ${r.status}`);
            loadHistory();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const adminSubscribe = async () => {
        if (!venueId) { toast.error("Select a venue"); return; }
        try {
            await endpoints.adminDigestSubscribe({ venue_id: venueId, enabled: true });
            toast.success("You're now subscribed to this venue's weekly digest.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="p-4 md:p-8" data-testid="admin-digests-page">
            <header className="border-b border-[#2a2a2a] pb-4 mb-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] flex items-center gap-1"><Mail className="w-3 h-3" /> Digests</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mt-1">Weekly follower digest</h1>
                <p className="mt-2 text-xs text-[#a3a3a3] max-w-2xl">Send a test digest to yourself or another user for a chosen venue. The scheduled Monday run at 08:00 UTC targets all opted-in venue managers automatically.</p>
            </header>

            <section className="border border-[#2a2a2a] p-4 space-y-3 max-w-3xl" data-testid="admin-digest-tester">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Send a test</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <div className="text-[10px] font-mono uppercase text-[#666] mb-1">Venue</div>
                        <select value={venueId} onChange={(e) => setVenueId(e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none font-mono text-sm" data-testid="admin-digest-venue-select">
                            <option value="" className="bg-[#141414]">— Choose a venue —</option>
                            {venues.map((v) => <option key={v.id} value={v.id} className="bg-[#141414]">{v.name}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <div className="text-[10px] font-mono uppercase text-[#666] mb-1">Recipient (defaults to you)</div>
                        <select value={userId} onChange={(e) => setUserId(e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none font-mono text-sm" data-testid="admin-digest-user-select">
                            <option value="" className="bg-[#141414]">Send to me</option>
                            {users.map((u) => <option key={u.id} value={u.id} className="bg-[#141414]">{u.email}</option>)}
                        </select>
                    </label>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={triggerTest} disabled={busy || !venueId} className="pm-btn pm-btn-primary" data-testid="admin-digest-trigger">
                        <Send className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Send test digest"}
                    </button>
                    <button onClick={adminSubscribe} disabled={!venueId} className="pm-btn" data-testid="admin-digest-subscribe">
                        Subscribe me to weekly runs
                    </button>
                </div>
                {lastResult && (
                    <pre className="text-[10px] font-mono text-[#a3a3a3] whitespace-pre-wrap max-h-40 overflow-auto border border-[#2a2a2a] p-2" data-testid="admin-digest-last-result">
{JSON.stringify({ status: lastResult.status, week_key: lastResult.week_key, ok: lastResult.ok }, null, 2)}
                    </pre>
                )}
            </section>

            <section className="mt-8">
                <div className="flex items-baseline justify-between mb-2">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Recent</div>
                        <h2 className="font-display font-black uppercase text-2xl">Send history</h2>
                    </div>
                    <button onClick={loadHistory} className="pm-btn text-xs" data-testid="admin-digest-refresh">
                        <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </button>
                </div>
                {loading ? (
                    <div className="font-mono text-xs text-[#666]" data-testid="admin-digest-loading">Loading…</div>
                ) : history.length === 0 ? (
                    <div className="border border-dashed border-[#2a2a2a] p-6 text-sm text-[#a3a3a3] text-center" data-testid="admin-digest-empty">No digest sends yet. Trigger a test or wait for Monday's scheduled run.</div>
                ) : (
                    <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]" data-testid="admin-digest-history">
                        {history.map((r) => (
                            <div key={r.id} className="p-3 flex flex-wrap items-center gap-2 text-xs font-mono" data-testid={`admin-digest-row-${r.id}`}>
                                <span className="text-[#a3a3a3]">{new Date(r.sent_at).toLocaleString()}</span>
                                <span className="text-[#f4f4f0] font-bold uppercase">{r.venue_name || r.venue_id}</span>
                                <span className="text-[#a3a3a3]">→ {r.user_email || r.user_id}</span>
                                <span className="text-[#666]">· {r.week_key}</span>
                                <span className={`ml-auto uppercase ${STATUS_STYLES[r.status] || "text-[#a3a3a3]"}`} data-testid={`admin-digest-status-${r.id}`}>{r.status}</span>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
